import { Home } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import type { DataTableColumn } from '@/admin/components/data-table'
import { useGroupProperties, type GroupPropertyRow } from '@/admin/hooks/use-property-groups'

/**
 * A group's properties as rows of the group list itself, under the group's
 * row and in its columns - the Product Library's product / variant pattern - so
 * a property reads as belonging to its group. Loaded only when it is opened.
 */
export function GroupPropertyRows<T>({
  groupUuid,
  visibleColumns,
}: {
  groupUuid: string
  visibleColumns: DataTableColumn<T>[]
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: properties, isLoading } = useGroupProperties(groupUuid)

  if (isLoading || !properties?.length) {
    return (
      <tr className="border-b-[0.5px] border-[var(--rule)]">
        <td
          colSpan={visibleColumns.length}
          className="py-2 pl-[76px] pr-4 text-[12px] text-[var(--ink-3)]"
        >
          {isLoading
            ? t('groups.home.loadingProperties', 'Loading properties...')
            : t('groups.detail.noProperties', 'No properties yet. Add an existing property or a new one.')}
        </td>
      </tr>
    )
  }

  function cell(col: DataTableColumn<T>, p: GroupPropertyRow) {
    switch (col.id) {
      case 'group':
        return (
          <td key={col.id} className="py-2 pl-[76px] pr-4">
            <span className="flex w-[260px] items-start gap-2 whitespace-normal text-[13px] text-[var(--ink-2)]">
              <Home className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--ink-3)]" />
              {p.address}
            </span>
          </td>
        )
      case 'contact':
        return (
          <td key={col.id} className="px-4 py-2 text-[13px] text-[var(--ink-2)]">
            <span className="block w-[180px] whitespace-normal">{p.owner_name || '-'}</span>
          </td>
        )
      case 'registered':
        return (
          <td key={col.id} className="px-4 py-2 text-[13px] text-[var(--ink-2)]">
            {p.registered_products}
          </td>
        )
      case 'claims':
        return (
          <td key={col.id} className="px-4 py-2 text-[13px] text-[var(--ink-2)]">
            {p.open_claims}
          </td>
        )
      case 'transfers':
        return (
          <td key={col.id} className="px-4 py-2 text-[13px] text-[var(--ink-2)]">
            {p.open_transfers}
          </td>
        )
      default:
        return <td key={col.id} className="px-4 py-2" />
    }
  }

  return (
    <>
      {properties.map((p) => (
        <tr
          key={p.uuid}
          onClick={() => navigate(`/properties/${p.uuid}`)}
          className="cursor-pointer border-b-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] transition-colors duration-100 hover:bg-[var(--bg-light)]"
        >
          {visibleColumns.map((col) => cell(col, p))}
        </tr>
      ))}
    </>
  )
}
